/*
 * ardam Partners: storefront link capture (theme asset on the live store).
 *
 * The Partners redirect (/r/{code}, /q/{code}) lands the shopper on the storefront with
 *   ?ref=<code>&apref=<signed token>
 * This script:
 *   1. captures `apref` (shape-checked only; the server verifies the signature) and `ref`;
 *   2. keeps them in first-party localStorage until the token's own expiry (30-day window);
 *   3. strips `apref` from the address bar so a copied/shared URL does not carry the token;
 *   4. writes them to the cart as hidden attributes `__apref` / `__ap_ref` (same-origin /cart
 *      endpoints only) and re-applies after cart add/change/clear, so they reach the order's
 *      `note_attributes`;
 *   5. when, and only when, a shopper activates a dynamic checkout button, adds the referral to that
 *      product form as hidden line item properties, which is what that path carries to the order.
 * It sends nothing to any third party, reads no customer data and never blocks the page.
 * The latest valid token in this browser wins; which partner earns is decided server-side.
 * It also leaves the stored referral on `window.__ardamPartnersReferral` (and fires
 * `ardam-partners:referral`) for the optional "Recommended by" script, recommended-by.js.
 */
(function (root) {
  'use strict';
  var KEY = 'ap_ref_v1';
  var TOKEN_RE = /^rs_[0-9A-HJKMNP-TV-Z]{26}\.(\d{13})\.[A-Za-z0-9_-]{1,32}\.[A-Za-z0-9_-]{43}$/;
  var CODE_RE = /^[2-9A-HJKMNP-Z]{7}$/;
  var CART_WRITE = /\/cart\/(add|change|clear|update)(\.js)?(\?|$)/;
  var DYNAMIC_BUTTON = '.shopify-payment-button, shopify-accelerated-checkout, shop-pay-wallet-button, more-payment-options-link, shopify-buy-it-now-button';
  var PRODUCT_FORM = 'form[action*="/cart/add"]';
  var ARM_MS = 5000;

  function tokenExpiry(token) {
    var m = TOKEN_RE.exec(token || '');
    return m ? Number(m[1]) : null;
  }

  function readStored(storage, now) {
    try {
      var v = JSON.parse(storage.getItem(KEY) || 'null');
      if (!v || typeof v.token !== 'string') return null;
      var exp = tokenExpiry(v.token);
      if (exp === null || exp <= now) { storage.removeItem(KEY); return null; }
      return {token: v.token, code: CODE_RE.test(v.code || '') ? v.code : null};
    } catch (e) { return null; }
  }

  /** Pure: decide what to keep, given the current URL and what is stored. */
  function capture(href, storage, now) {
    var url;
    try { url = new URL(href); } catch (e) { return {state: readStored(storage, now), cleanUrl: null}; }
    var token = url.searchParams.get('apref');
    var code = (url.searchParams.get('ref') || '').trim().toUpperCase();
    var cleanUrl = null;
    if (token !== null) {
      url.searchParams.delete('apref');
      cleanUrl = url.pathname + (url.search ? url.search : '') + url.hash;
      var exp = tokenExpiry(token);
      if (exp !== null && exp > now) {
        var fresh = {token: token, code: CODE_RE.test(code) ? code : null};
        try { storage.setItem(KEY, JSON.stringify({token: fresh.token, code: fresh.code, capturedAt: now})); } catch (e) { /* storage full or blocked */ }
        return {state: fresh, cleanUrl: cleanUrl};
      }
    }
    return {state: readStored(storage, now), cleanUrl: cleanUrl};
  }

  /** Writes the attributes only when the cart does not already carry this token. */
  function applyToCart(fetchImpl, base, state) {
    if (!state) return Promise.resolve('none');
    return fetchImpl(base + 'cart.js', {credentials: 'same-origin', headers: {Accept: 'application/json'}})
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        if (attrs.__apref === state.token && (attrs.__ap_ref || null) === state.code) return 'unchanged';
        var body = {attributes: {__apref: state.token}};
        if (state.code) body.attributes.__ap_ref = state.code;
        return fetchImpl(base + 'cart/update.js', {method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)})
          .then(function (r) { return r.ok ? 'written' : 'failed'; });
      })
      .catch(function () { return 'failed'; });
  }

  /*
   * Dynamic checkout (Buy it now, More payment options, Shop Pay) leaves the page without the cart, so cart attributes never
   * reach that order. Shopify does carry the product form's line item properties, so at the moment a shopper activates one of
   * these buttons, and only then, the referral goes on that form as hidden `_apref` / `_ap_ref` properties (the leading
   * underscore hides them at checkout). Any other click, a submit by another button, a short timer or a return to the page
   * takes them off again, so Add to cart never carries them and cart lines are never split. With no stored referral nothing happens.
   */
  function dynamicCheckoutForm(event) {
    var path = event.composedPath ? event.composedPath() : [event.target];
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (el && el.matches && el.matches(DYNAMIC_BUTTON)) return el.closest(PRODUCT_FORM);
    }
    return null;
  }

  function watchDynamicCheckout(doc, win, getState) {
    var added = [];
    var timer = null;
    function disarm() {
      if (timer !== null) { win.clearTimeout(timer); timer = null; }
      while (added.length) { var i = added.pop(); if (i.parentNode) i.parentNode.removeChild(i); }
    }
    function arm(form) {
      disarm();
      var state = getState();
      if (!state) return;
      var props = [['_apref', state.token]];
      if (state.code) props.push(['_ap_ref', state.code]);
      for (var i = 0; i < props.length; i++) {
        var input = doc.createElement('input');
        input.type = 'hidden'; input.name = 'properties[' + props[i][0] + ']'; input.value = props[i][1];
        form.appendChild(input); added.push(input);
      }
      timer = win.setTimeout(disarm, ARM_MS);
    }
    function onActivate(event) {
      try {
        if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
        var form = dynamicCheckoutForm(event);
        if (form) arm(form); else if (event.type !== 'keydown') disarm();
      } catch (e) { /* never in the way of the page */ }
    }
    doc.addEventListener('pointerdown', onActivate, true);
    doc.addEventListener('click', onActivate, true);
    doc.addEventListener('keydown', onActivate, true);
    doc.addEventListener('submit', function (event) {
      if (!event.submitter || !event.submitter.matches || !event.submitter.closest(DYNAMIC_BUTTON)) disarm();
    }, true);
    win.addEventListener('pageshow', disarm);
  }

  /** Page-lifetime storage for browsers that block localStorage: the current visit can still reach the cart. */
  function pageStorage() {
    var data = {};
    return {getItem: function (k) { return Object.prototype.hasOwnProperty.call(data, k) ? data[k] : null; },
      setItem: function (k, v) { data[k] = String(v); }, removeItem: function (k) { delete data[k]; }};
  }

  function run(win) {
    var now = Date.now();
    var storage;
    try { storage = win.localStorage; storage.getItem(KEY); } catch (e) { storage = pageStorage(); }
    if (!storage) storage = pageStorage();
    var result = capture(win.location.href, storage, now);
    if (result.cleanUrl !== null && win.history && win.history.replaceState) {
      try { win.history.replaceState(win.history.state, '', result.cleanUrl); } catch (e) { /* ignore */ }
    }
    var base = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
    var pending = false;
    function reapply() {
      if (pending) return;
      pending = true;
      win.setTimeout(function () {
        pending = false;
        applyToCart(win.fetch.bind(win), base, readStored(storage, Date.now()));
      }, 300);
    }
    applyToCart(win.fetch.bind(win), base, result.state);
    // Hand the stored referral to the optional "Recommended by" script (recommended-by.js), if loaded.
    win.__ardamPartnersReferral = result.state;
    try { if (win.dispatchEvent && win.CustomEvent) win.dispatchEvent(new win.CustomEvent('ardam-partners:referral')); } catch (e) { /* ignore */ }
    if (win.document && win.document.addEventListener) watchDynamicCheckout(win.document, win, function () { return readStored(storage, Date.now()); });
    // Re-apply after the theme writes to the cart (a cleared or new cart loses attributes).
    if (win.PerformanceObserver) {
      try {
        new win.PerformanceObserver(function (list) {
          var entries = list.getEntries();
          for (var i = 0; i < entries.length; i++) {
            if (CART_WRITE.test(entries[i].name) && !/cart\/update/.test(entries[i].name)) { reapply(); return; }
          }
        }).observe({type: 'resource', buffered: false});
      } catch (e) { /* unsupported */ }
    }
  }

  var api = {capture: capture, applyToCart: applyToCart, readStored: readStored, tokenExpiry: tokenExpiry, run: run, watchDynamicCheckout: watchDynamicCheckout, KEY: KEY};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else if (root && root.document) run(root);
})(typeof window !== 'undefined' ? window : undefined);
